/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Realisations du site UK.
 *
 * Les projets viennent des traductions, ce qui permet de les decrire dans les
 * quatre langues. Le filtrage par categorie se fait cote client : la liste est
 * courte, et un aller-retour serveur serait plus lent que le rendu lui-meme.
 */
import React, { useMemo, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import MEDIA from "../lib/media";
import { Filter } from "lucide-react";
import ProductCards from "../components/ProductCards";
import { PRODUCT_UI, tx } from "../lib/products";

const Projects = () => {
  const { t, lang } = useLang();
  const [activeCountry, setActiveCountry] = useState("all");
  const [activeDomain, setActiveDomain] = useState("all");

  const projects = useMemo(() => t?.projects?.items || [], [t]);
  const countries = useMemo(
    () => Array.from(new Set(projects.map((p) => p.country))),
    [projects]
  );
  const domains = useMemo(
    () => Array.from(new Set(projects.map((p) => p.domain))),
    [projects]
  );

  if (!t) return null;
  const meta = t.meta.pages.projects;

  const filtered = projects.filter(
    (p) =>
      (activeCountry === "all" || p.country === activeCountry) &&
      (activeDomain === "all" || p.domain === activeDomain)
  );

  const ChipGroup = ({ label, value, setValue, options, testIdPrefix }) => (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 me-2">
        {label}
      </span>
      <button
        data-testid={`${testIdPrefix}-all`}
        onClick={() => setValue("all")}
        className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
          value === "all"
            ? "bg-slate-950 text-white border-slate-950"
            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
        }`}
      >
        {t.projects.filters.all}
      </button>
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`${testIdPrefix}-${opt}`}
          onClick={() => setValue(opt)}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
            value === opt
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  return (
    <main data-testid="projects-page" className="pt-20">
      <SEO title={meta.title} description={meta.description} lang={lang} />

      <section className="bg-slate-950 text-white py-24 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400">
            {t.projects.intro.eyebrow}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            {t.projects.intro.title}
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">{t.projects.intro.subtitle}</p>
        </div>
      </section>

      <section id="logiciels" data-testid="projects-products" className="py-20 lg:py-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
              {tx(PRODUCT_UI.eyebrow, lang)}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
              {tx(PRODUCT_UI.sectionTitle, lang)}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{tx(PRODUCT_UI.sectionSubtitle, lang)}</p>
          </div>
          <ProductCards lang={lang} />
        </div>
      </section>

      <section className="py-12 border-b border-slate-200 bg-slate-50">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="flex items-center gap-2 text-slate-700">
            <Filter className="h-4 w-4" />
            <span className="text-sm font-semibold uppercase tracking-widest">
              {lang === "fr" ? "Filtrer" : lang === "es" ? "Filtrar" : lang === "ar" ? "تصفية" : "Filter"}
            </span>
          </div>
          <ChipGroup
            label={t.projects.filters.by_country}
            value={activeCountry}
            setValue={setActiveCountry}
            options={countries}
            testIdPrefix="filter-country"
          />
          <ChipGroup
            label={t.projects.filters.by_domain}
            value={activeDomain}
            setValue={setActiveDomain}
            options={domains}
            testIdPrefix="filter-domain"
          />
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <p data-testid="projects-empty" className="text-center text-slate-500 py-20">
              —
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <article
                  key={p.slug}
                  data-testid={`project-card-${p.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden card-lift"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={MEDIA[p.image_key]}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                        {p.country}
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                        {p.domain}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-950 mb-3">{p.title}</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                          {lang === "fr" ? "Contexte" : lang === "es" ? "Contexto" : lang === "ar" ? "السياق" : "Context"}
                        </span>
                        <p className="mt-1 text-slate-600 leading-relaxed">{p.context}</p>
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                          {lang === "fr" ? "Solution" : lang === "es" ? "Solución" : lang === "ar" ? "الحل" : "Solution"}
                        </span>
                        <p className="mt-1 text-slate-700 leading-relaxed">{p.solution}</p>
                      </div>
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-cyan-600">
                          {lang === "fr" ? "Résultat" : lang === "es" ? "Resultado" : lang === "ar" ? "النتيجة" : "Result"}
                        </span>
                        <p className="mt-1 text-slate-700 leading-relaxed font-medium">{p.result}</p>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Projects;
